import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchStaffKpis, fetchTenantStaffPage } from '@services/registry';

export function useStaffDirectory({ tenantId, page = 1, pageSize = 10, includeRows = true }) {
    const cursorByPageRef = useRef(new Map([[1, null]]));

    useEffect(() => {
        cursorByPageRef.current = new Map([[1, null]]);
    }, [tenantId, pageSize]);

    const staffQuery = useQuery({
        queryKey: ['staff', tenantId, page, pageSize],
        queryFn: async () => {
            const cursor = cursorByPageRef.current.get(page);
            if (page > 1 && typeof cursor === 'undefined') {
                throw new Error('Pagination cursor missing. Please go page-by-page.');
            }
            const res = await fetchTenantStaffPage({
                tenantId,
                pageSize,
                cursor: cursor ?? null,
            });
            cursorByPageRef.current.set(page + 1, res.nextCursor || null);
            return res;
        },
        enabled: !!tenantId && includeRows,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const kpisQuery = useQuery({
        queryKey: ['staffKpis', tenantId],
        queryFn: () => fetchStaffKpis({ tenantId }),
        enabled: !!tenantId,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        rows: includeRows ? (staffQuery.data?.rows || []) : [],
        total: includeRows ? (staffQuery.data?.total || 0) : (kpisQuery.data?.totalStaff || 0),
        kpis: kpisQuery.data || { totalStaff: 0, activeStaff: 0, inactiveStaff: 0 },
        loading: includeRows ? (staffQuery.isLoading || kpisQuery.isLoading) : kpisQuery.isLoading,
        error: staffQuery.error || kpisQuery.error || null,
    };
}

